// Auth Store - Manages authentication state only
// API calls are handled by useAuth composable

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User } from '@/domains/auth/types/User';
import type { LoginResponse } from '@/domains/auth/types/Login';
import type { OptInSuccessPayload } from '@/domains/auth/types/OptIn';

const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_GUID_STORAGE_KEY = 'userGuid';

export const useAuthStore = defineStore('auth', () => {
  // State
  const loading = ref(false);
  const error = ref<string | null>(null);
  const verificationToken = ref<string | null>(null);
  const accessToken = ref<string | null>(null); // Stored in memory only
  const user = ref<User | null>(null);
  const isReconnecting = ref(false);
  const optInSuccessPayload = ref<OptInSuccessPayload | null>(null);
  const smsSent = ref<boolean>(true);
  const emailSent = ref<boolean>(true);

  const clearError = () => {
    error.value = null;
  };

  const reset = () => {
    loading.value = false;
    error.value = null;
    verificationToken.value = null;
    accessToken.value = null;
    user.value = null;
    isReconnecting.value = false;
    smsSent.value = true;
    emailSent.value = true;
  };

  const setReconnecting = (value: boolean) => {
    isReconnecting.value = value;
  };

  const setOptInSuccessPayload = (payload: OptInSuccessPayload | null) => {
    optInSuccessPayload.value = payload;
    if (payload) {
      localStorage.setItem('optInSuccess', JSON.stringify(payload));
    } else {
      localStorage.removeItem('optInSuccess');
    }
  };

  const setAccessToken = (token: string) => {
    accessToken.value = token;
  };

  const setRefreshToken = (token: string) => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  };

  const getRefreshToken = (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  };

  const setUser = (userData: User | null) => {
    user.value = userData;
  };

  const setVerificationToken = (token: string | null) => {
    verificationToken.value = token;
  };

  const setSmsSent = (value: boolean) => {
    smsSent.value = value;
  };

  const setEmailSent = (value: boolean) => {
    emailSent.value = value;
  };

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage;
  };

  const login = (response: LoginResponse) => {
    accessToken.value = response.accessToken;
    setRefreshToken(response.refreshToken);
    user.value = response.user;

    // Backup: Ensure UUID is preserved when login state is set
    // This is a safety mechanism - UUID should already be stored by useAuth composable
    // but this ensures it persists even if something goes wrong in the composable
    try {
      const existingGuid = localStorage.getItem(USER_GUID_STORAGE_KEY);
      if (!existingGuid) {
        if (import.meta.env.DEV) {
          console.warn('[AuthStore] Login called but no UUID found in storage');
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[AuthStore] Error checking UUID storage:', error);
      }
    }
  };

  const logout = () => {
    accessToken.value = null;
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    user.value = null;
  };

  const loadFromStorage = (): boolean => {
    return localStorage.getItem(REFRESH_TOKEN_KEY) !== null;
  };

  const state = {
    accessToken: computed(() => accessToken.value),
    user: computed(() => user.value),
    verificationToken: computed(() => verificationToken.value),
    optInSuccessPayload: computed(() => optInSuccessPayload.value),
    smsSent: computed(() => smsSent.value),
    emailSent: computed(() => emailSent.value),
  };

  const selectors = {};

  const actions = {
    clearError,
    reset,
    setReconnecting,
    setOptInSuccessPayload,
    setAccessToken,
    setRefreshToken,
    getRefreshToken,
    setUser,
    setVerificationToken,
    setSmsSent,
    setEmailSent,
    setError,
    login,
    logout,
    loadFromStorage,
  };

  const status = {
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    isReconnecting: computed(() => isReconnecting.value),
  };

  return {
    state,
    selectors,
    actions,
    status,
  };
});
